export type PrivilegedAccessRequirement =
  | "finance_view"
  | "finance_create"
  | "dues_operational"
  | "billing_create_operational"
  | "billing_own_view"
  | "treatment_plan_view"
  | "treatment_plan_create";

export interface PrivilegedAccessAssignment {
  standardRole?: string | null;
  legacyRole?: string | null;
  accessMode?: string | null;
}

export interface PrivilegedPageAccessInput {
  isSuperAdmin?: boolean | null;
  assignments?: PrivilegedAccessAssignment[];
}

export interface PrivilegedPageAccess {
  canViewBilling: boolean;
  canCreateBilling: boolean;
  canCreateOperationalBills: boolean;
  canViewOwnCreatedBills: boolean;
  canCollectOperationalDues: boolean;
  canViewPatientFileBilling: boolean;
  canViewTreatmentPlans: boolean;
  canCreateTreatmentPlans: boolean;
  canAccessRequirement: (requirement?: PrivilegedAccessRequirement) => boolean;
}

const MANAGER_LEGACY_ROLES = new Set([
  "admin",
  "clinic_manager",
  "cluster_manager",
  "clinic_incharge",
]);

const isFullAccess = (accessMode?: string | null) => (accessMode || "full") === "full";
const isAccountant = (assignment: PrivilegedAccessAssignment) => assignment.legacyRole === "accountant";
const isFrontDesk = (assignment: PrivilegedAccessAssignment) => {
  if (!isFullAccess(assignment.accessMode)) return false;
  return (
    assignment.standardRole === "front_desk" ||
    assignment.legacyRole === "front_desk" ||
    assignment.legacyRole === "front_office"
  );
};

const isOwnerOrManager = (assignment: PrivilegedAccessAssignment) => {
  if (!isFullAccess(assignment.accessMode)) return false;
  if (assignment.standardRole === "owner") return true;
  if (assignment.standardRole === "manager" && !isAccountant(assignment)) return true;
  return assignment.legacyRole ? MANAGER_LEGACY_ROLES.has(assignment.legacyRole) : false;
};

export const getPrivilegedPageAccess = ({
  isSuperAdmin,
  assignments = [],
}: PrivilegedPageAccessInput): PrivilegedPageAccess => {
  const hasManagerAccess = Boolean(isSuperAdmin) || assignments.some(isOwnerOrManager);
  const hasAccountantViewAccess = assignments.some(
    (assignment) => isFullAccess(assignment.accessMode) && isAccountant(assignment)
  );
  const hasPrivilegedViewAccess = hasManagerAccess || hasAccountantViewAccess;
  const hasOperationalBillingAccess = hasManagerAccess || assignments.some(isFrontDesk);
  const hasOperationalDuesAccess = hasPrivilegedViewAccess || assignments.some(isFrontDesk);
  const hasPatientFileBillingAccess = hasPrivilegedViewAccess || assignments.some(isFrontDesk);

  const access = {
    canViewBilling: hasPrivilegedViewAccess,
    canCreateBilling: hasManagerAccess,
    canCreateOperationalBills: hasOperationalBillingAccess,
    canViewOwnCreatedBills: hasOperationalBillingAccess || hasPrivilegedViewAccess,
    canCollectOperationalDues: hasOperationalDuesAccess,
    canViewPatientFileBilling: hasPatientFileBillingAccess,
    canViewTreatmentPlans: hasPrivilegedViewAccess,
    canCreateTreatmentPlans: hasManagerAccess,
  };

  return {
    ...access,
    canAccessRequirement: (requirement?: PrivilegedAccessRequirement) => {
      if (!requirement) return true;
      switch (requirement) {
        case "finance_view":
          return access.canViewBilling;
        case "finance_create":
          return access.canCreateBilling;
        case "dues_operational":
          return access.canCollectOperationalDues;
        case "billing_create_operational":
          return access.canCreateOperationalBills;
        case "billing_own_view":
          return access.canViewOwnCreatedBills;
        case "treatment_plan_view":
          return access.canViewTreatmentPlans;
        case "treatment_plan_create":
          return access.canCreateTreatmentPlans;
        default:
          return false;
      }
    },
  };
};
